import {
  FILTER_THRESHOLD,
  SCROLL_THRESHOLD,
  filterOptions,
  needsFilter,
  needsScroll,
  toggleOption
} from '../options';

describe('pill group options', () => {
  const build = (count: number) =>
    Array.from({ length: count }, (_, i) => ({ id: `id-${i}`, name: `Option ${i}` }));

  describe('needsFilter', () => {
    it('leaves short lists without a filter', () => {
      expect(needsFilter(build(FILTER_THRESHOLD))).toBe(false);
    });

    it('adds a filter once the list is longer than the threshold', () => {
      expect(needsFilter(build(FILTER_THRESHOLD + 1))).toBe(true);
    });

    it('handles a missing list', () => {
      expect(needsFilter()).toBe(false);
    });
  });

  describe('needsScroll', () => {
    it('leaves a list at the threshold at its natural height', () => {
      expect(needsScroll(build(SCROLL_THRESHOLD))).toBe(false);
    });

    it('caps the height once the list is longer than the threshold', () => {
      expect(needsScroll(build(SCROLL_THRESHOLD + 1))).toBe(true);
    });

    it('caps later than the filter appears, so mid-length lists stay fully visible', () => {
      expect(SCROLL_THRESHOLD).toBeGreaterThan(FILTER_THRESHOLD);
    });

    it('handles a missing list', () => {
      expect(needsScroll()).toBe(false);
    });

    // The cap is measured against what is on screen, not the whole list, so a filter
    // that barely narrows a long list does not let it expand to full height
    it('still caps a filtered result set that stays long', () => {
      const options = build(SCROLL_THRESHOLD * 3);

      expect(needsScroll(filterOptions(options, 'Option'))).toBe(true);
    });

    it('releases the cap once the filter genuinely narrows the list', () => {
      const options = build(SCROLL_THRESHOLD * 3);

      expect(needsScroll(filterOptions(options, 'Option 1'))).toBe(false);
    });
  });

  describe('filterOptions', () => {
    const options = [
      { id: 'hiking', name: 'Hiking' },
      { id: 'biking', name: 'Biking' },
      { id: 'swimming', name: 'Swimming' }
    ];

    it('returns everything when there is no query', () => {
      expect(filterOptions(options, '')).toEqual(options);
      expect(filterOptions(options, '   ')).toEqual(options);
    });

    it('matches on any part of the name, ignoring case', () => {
      expect(filterOptions(options, 'IKING').map((o) => o.id)).toEqual(['hiking', 'biking']);
    });

    it('returns nothing when no name matches', () => {
      expect(filterOptions(options, 'kayak')).toEqual([]);
    });

    it('skips options without a name rather than throwing', () => {
      const ragged = [...options, { id: 'nameless', name: null } as any];

      expect(filterOptions(ragged, 'hiking').map((o) => o.id)).toEqual(['hiking']);
    });
  });

  describe('toggleOption', () => {
    it('adds to the selection when picking a new option', () => {
      expect(toggleOption(['a'], 'b')).toEqual(['a', 'b']);
    });

    it('removes from the selection when picking a selected option', () => {
      expect(toggleOption(['a', 'b'], 'a')).toEqual(['b']);
    });

    it('replaces the selection in single mode', () => {
      expect(toggleOption(['a'], 'b', true)).toEqual(['b']);
    });

    it('clears the selection when the selected option is picked again in single mode', () => {
      expect(toggleOption(['a'], 'a', true)).toEqual([]);
    });

    it('does not mutate the selection it was given', () => {
      const selected = ['a'];
      toggleOption(selected, 'b');

      expect(selected).toEqual(['a']);
    });
  });
});
